// Thoracic Spine (Mid-Back) Template Configuration
// Questions simplified to 12-year-old reading level

export const thoracicTemplate = {
  id: 'THORACIC_SPINE',
  name: 'Mid-Back',
  icon: '🦴',
  dominantSide: 'hand',

  fields: {
    problemSide: {
      type: 'radio',
      label: 'Which side of your mid-back is affected?',
      options: ['Right side', 'Left side', 'Middle', 'Both sides'],
      required: true
    },

    problemDate: {
      type: 'date',
      label: 'When did your mid-back problem start?',
      required: true
    },

    onsetType: {
      type: 'radio',
      label: 'How did it start?',
      options: ['Suddenly', 'Slowly over time'],
      required: true
    },

    mainProblem: {
      type: 'checkbox-multiple',
      label: 'What problems are you having? (tick all that apply)',
      options: [
        'Mid-back pain',
        'Stiffness',
        'My back looks curved',
        'Tingling or numbness',
        'Weakness',
        'Other'
      ]
    },

    mainProblemOther: {
      type: 'text',
      label: 'Other problem (please describe):',
      showWhen: { field: 'mainProblem', includes: 'Other' }
    },

    problemDescription: {
      type: 'textarea',
      label: 'Tell us what happened:',
      placeholder: 'Describe how your mid-back problem started',
      rows: 3,
      required: true
    },

    // Pain Section
    painLocation: {
      type: 'checkbox-multiple',
      label: 'Where exactly do you feel pain?',
      options: [
        'Middle of back (spine)',
        'Right side of back',
        'Left side of back',
        'Between shoulder blades',
        'Wrapping around ribs',
        'Into my chest'
      ]
    },

    painType: {
      type: 'checkbox-multiple',
      label: 'What does your pain feel like?',
      options: ['Aching', 'Sharp', 'Burning', 'Stabbing', 'Other']
    },

    painSeverity: {
      type: 'radio',
      label: 'How bad is your pain? (0 = no pain, 10 = worst pain ever)',
      options: ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10']
    },

    painRadiation: {
      type: 'radio',
      label: 'Does the pain travel anywhere else?',
      options: ['No, stays in my back', 'Yes, wraps around my ribs', 'Yes, goes into my chest', 'Yes, goes down my leg']
    },

    painTiming: {
      type: 'checkbox-multiple',
      label: 'When is your pain worst?',
      options: [
        'All the time',
        'In the morning',
        'At night',
        'When I move or twist',
        'After sitting for a long time',
        'When I take a deep breath'
      ]
    },

    painWorsening: {
      type: 'checkbox-multiple',
      label: 'What makes your pain worse?',
      options: [
        'Twisting',
        'Bending forward',
        'Sitting',
        'Coughing or sneezing',
        'Deep breathing',
        'Lifting things',
        'Other'
      ]
    },

    painRelieving: {
      type: 'checkbox-multiple',
      label: 'What helps your pain feel better?',
      options: [
        'Rest',
        'Heat',
        'Cold/ice',
        'Pain medication',
        'Lying down',
        'Stretching',
        'Other'
      ]
    },

    // Stiffness Section
    hasStiffness: {
      type: 'radio',
      label: 'Does your mid-back feel stiff?',
      options: ['Yes', 'No']
    },

    stiffnessWorst: {
      type: 'radio',
      label: 'When is the stiffness worst?',
      options: ['In the morning', 'At night', 'After sitting', 'All the time'],
      showWhen: { field: 'hasStiffness', equals: 'Yes' }
    },

    morningStiffnessDuration: {
      type: 'radio',
      label: 'How long does the morning stiffness last?',
      options: ['Less than 30 minutes', '30-60 minutes', 'More than 1 hour'],
      showWhen: { field: 'hasStiffness', equals: 'Yes' }
    },

    stiffnessMovements: {
      type: 'checkbox-multiple',
      label: 'Which movements are hard to do?',
      options: [
        'Bending forward',
        'Bending backward',
        'Twisting',
        'Reaching up',
        'Taking a deep breath'
      ],
      showWhen: { field: 'hasStiffness', equals: 'Yes' }
    },

    // Neurological Symptoms
    hasNumbness: {
      type: 'radio',
      label: 'Do you have any pins and needles or numbness?',
      options: ['Yes', 'No']
    },

    numbnessLocation: {
      type: 'text',
      label: 'Where do you feel the pins and needles or numbness?',
      placeholder: 'e.g., around my ribs, in my legs',
      showWhen: { field: 'hasNumbness', equals: 'Yes' }
    },

    hasWeakness: {
      type: 'radio',
      label: 'Do you have any weakness in your legs?',
      options: ['Yes', 'No']
    },

    weaknessDescription: {
      type: 'text',
      label: 'What kind of weakness do you notice?',
      placeholder: 'e.g., legs feel heavy, trouble climbing stairs',
      showWhen: { field: 'hasWeakness', equals: 'Yes' }
    },

    balanceProblems: {
      type: 'radio',
      label: 'Do you have trouble walking or keeping your balance?',
      options: ['Yes', 'No']
    },

    bladderBowelProblems: {
      type: 'radio',
      label: 'Do you have any trouble controlling your bladder or bowels?',
      options: ['Yes', 'No']
    },

    // Posture/Shape Section
    shapeChange: {
      type: 'radio',
      label: 'Has the shape of your back changed?',
      options: ['Yes', 'No', 'Not sure']
    },

    shapeChangeDetails: {
      type: 'textarea',
      label: 'Tell us about the shape change:',
      placeholder: 'When did you notice it? Is it getting worse?',
      rows: 2,
      showWhen: { field: 'shapeChange', equals: 'Yes' }
    },

    shapeChangeAfter: {
      type: 'radio',
      label: 'Did the shape change happen after an injury, infection, or surgery?',
      options: ['Yes', 'No'],
      showWhen: { field: 'shapeChange', equals: 'Yes' }
    },

    // Functional Limitations
    dailyActivities: {
      type: 'checkbox-multiple',
      label: 'What can\'t you do because of your mid-back problem?',
      options: [
        'Sitting for long periods',
        'Standing for long periods',
        'Bending or twisting',
        'Lifting things',
        'Sleeping comfortably',
        'Exercising or sports',
        'Working',
        'None - I can do everything okay'
      ]
    },

    sleepingDifficulty: {
      type: 'radio',
      label: 'Does your mid-back affect your sleep?',
      options: ['Yes', 'No']
    },

    // Previous History
    previousBackProblems: {
      type: 'radio',
      label: 'Have you had back problems before?',
      options: ['Yes', 'No']
    },

    previousBackDetails: {
      type: 'textarea',
      label: 'Tell us about your previous back problems:',
      rows: 2,
      showWhen: { field: 'previousBackProblems', equals: 'Yes' }
    },

    backSurgery: {
      type: 'radio',
      label: 'Have you ever had back surgery?',
      options: ['Yes', 'No']
    },

    backSurgeryDetails: {
      type: 'text',
      label: 'What surgery did you have?',
      showWhen: { field: 'backSurgery', equals: 'Yes' }
    },

    recentInjury: {
      type: 'radio',
      label: 'Have you had a recent injury or accident?',
      options: ['Yes', 'No']
    },

    recentInjuryDetails: {
      type: 'textarea',
      label: 'Tell us about the injury:',
      rows: 2,
      showWhen: { field: 'recentInjury', equals: 'Yes' }
    },

    // Medical History
    medicalConditions: {
      type: 'checkbox-multiple',
      label: 'Do you have any of these conditions?',
      options: [
        'Osteoporosis (weak bones)',
        'Ankylosing spondylitis (spinal arthritis)',
        'Rheumatoid arthritis',
        'Scoliosis (curved spine)',
        'Scheuermann\'s disease',
        'Cancer',
        'None of these'
      ]
    },

    currentMedications: {
      type: 'textarea',
      label: 'What medications do you take regularly?',
      placeholder: 'List all your current medications',
      rows: 2
    },

    painMedications: {
      type: 'radio',
      label: 'Have you taken any painkillers or had any injections for this problem?',
      options: ['Yes', 'No']
    },

    painMedicationsDetails: {
      type: 'text',
      label: 'What painkillers or injections have you had?',
      showWhen: { field: 'painMedications', equals: 'Yes' }
    },

    allergies: {
      type: 'text',
      label: 'Do you have any allergies?',
      placeholder: 'List any allergies to medications or other things'
    },

    // Family History
    familyHistory: {
      type: 'radio',
      label: 'Does anyone in your family have spine or joint problems?',
      options: ['Yes', 'No', 'Not sure']
    },

    familyHistoryDetails: {
      type: 'text',
      label: 'What spine or joint problems do they have?',
      showWhen: { field: 'familyHistory', equals: 'Yes' }
    },

    // Lifestyle
    occupation: {
      type: 'text',
      label: 'What is your job or what do you do at school?',
      placeholder: 'Tell us what you do for work or school'
    },

    workPosture: {
      type: 'radio',
      label: 'Does your job or school involve sitting at a desk?',
      options: ['Yes, most of the day', 'Yes, some of the time', 'No']
    },

    sport: {
      type: 'text',
      label: 'What sports or exercise do you do?',
      placeholder: 'e.g., swimming, rugby, gym, running'
    },

    activityLevel: {
      type: 'radio',
      label: 'How active are you normally?',
      options: ['Very active', 'Moderately active', 'Not very active', 'Inactive']
    },

    // Other Symptoms (Red Flags)
    breathingProblems: {
      type: 'radio',
      label: 'Do you have any breathing problems?',
      options: ['Yes', 'No']
    },

    chestPain: {
      type: 'radio',
      label: 'Do you have any chest pain?',
      options: ['Yes', 'No']
    },

    stomachProblems: {
      type: 'radio',
      label: 'Do you have any tummy pain or reflux?',
      options: ['Yes', 'No']
    },

    fever: {
      type: 'radio',
      label: 'Have you had a fever, unexplained weight loss, or feel very tired?',
      options: ['Yes', 'No']
    },

    nightPain: {
      type: 'radio',
      label: 'Do you have severe pain at night that wakes you up?',
      options: ['Yes', 'No']
    }
  },

  sections: [
    {
      id: 'consent',
      title: '0. Consent & Agreements',
      component: 'ConsentSection',
      enabled: true
    },
    {
      id: 'demographics',
      title: '1. Your Details',
      component: 'DemographicsSection',
      enabled: true
    },
    {
      id: 'problem',
      title: '2. About Your Mid-Back Problem',
      component: 'GenericSection',
      enabled: true,
      fields: ['problemDate', 'problemSide', 'onsetType', 'mainProblem', 'mainProblemOther', 'problemDescription']
    },
    {
      id: 'pain',
      title: '3. Your Pain',
      component: 'GenericSection',
      enabled: true,
      fields: ['painLocation', 'painType', 'painSeverity', 'painRadiation', 'painTiming', 'painWorsening', 'painRelieving']
    },
    {
      id: 'stiffness',
      title: '4. Stiffness & Movement',
      component: 'GenericSection',
      enabled: true,
      fields: ['hasStiffness', 'stiffnessWorst', 'morningStiffnessDuration', 'stiffnessMovements']
    },
    {
      id: 'nerves',
      title: '5. Nerves & Balance',
      component: 'GenericSection',
      enabled: true,
      fields: ['hasNumbness', 'numbnessLocation', 'hasWeakness', 'weaknessDescription', 'balanceProblems']
    },
    {
      id: 'posture',
      title: '6. Shape & Posture',
      component: 'GenericSection',
      enabled: true,
      fields: ['shapeChange', 'shapeChangeDetails', 'shapeChangeAfter']
    },
    {
      id: 'redFlags',
      title: '7. Important Safety Questions',
      component: 'RedFlagsSection',
      enabled: true,
      fields: [
        { id: 'bladderBowelProblems', label: 'New bladder or bowel problems', weight: 10, urgent: true },
        { id: 'hasWeakness', label: 'Serious weakness in legs', weight: 8, urgent: true },
        { id: 'fever', label: 'Fever, weight loss, or severe tiredness', weight: 7, urgent: true },
        { id: 'nightPain', label: 'Severe night pain', weight: 6 },
        { id: 'chestPain', label: 'Chest pain', weight: 7, urgent: true },
        { id: 'breathingProblems', label: 'Breathing problems', weight: 6 }
      ],
      alerts: {
        critical: { threshold: 10, message: 'URGENT: These symptoms need immediate medical attention' },
        urgent: { threshold: 6, message: 'Please contact us promptly about these symptoms' }
      }
    },
    {
      id: 'functional',
      title: '8. What You Can & Can\'t Do',
      component: 'GenericSection',
      enabled: true,
      fields: ['dailyActivities', 'sleepingDifficulty']
    },
    {
      id: 'history',
      title: '9. Previous Problems',
      component: 'GenericSection',
      enabled: true,
      fields: ['previousBackProblems', 'previousBackDetails', 'backSurgery', 'backSurgeryDetails', 'recentInjury', 'recentInjuryDetails']
    },
    {
      id: 'medical',
      title: '10. Your Health',
      component: 'MedicalHistorySection',
      enabled: true
    },
    {
      id: 'lifestyle',
      title: '11. Daily Life',
      component: 'GenericSection',
      enabled: true,
      fields: ['occupation', 'workPosture', 'sport', 'activityLevel']
    },
    {
      id: 'otherSymptoms',
      title: '12. Other Symptoms',
      component: 'GenericSection',
      enabled: true,
      fields: ['breathingProblems', 'stomachProblems']
    },    {
      id: 'exerciseVitalSign',
      title: '13. Exercise as a Vital Sign',
      component: 'ExerciseVitalSignSection',
      enabled: true
    },

    {
      id: 'goals',
      title: '14. Your Goals',
      component: 'GoalsSection',
      enabled: true
    }
  ],

  letterTemplate: {
    mechanismLanguage: {
      'Suddenly': 'acute onset',
      'Slowly over time': 'gradual onset'
    },
    commonDiagnoses: [
      'Thoracic disc herniation',
      'Thoracic facet joint pain',
      'Costochondritis',
      'Thoracic spondylosis',
      'Scheuermann\'s disease',
      'Thoracic muscle strain',
      'Postural thoracic pain',
      'Thoracic radiculopathy',
      'Rib dysfunction'
    ]
  }
};

export default thoracicTemplate;
